import React from 'react';
import styled from 'styled-components';
import { FiClock, FiCheckCircle, FiAlertCircle, FiInfo, FiDownload, FiFileText, FiArchive } from 'react-icons/fi';

const TaskLogContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5em;
  padding: 1em 0;
`;

const ProgressSection = styled.div`
  background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.02)" : "rgba(255, 255, 255, 0.03)"};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 1.5em;
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5em;
  
  .stage-text {
    font-size: 0.95em;
    color: ${({ theme }) => theme.text};
    opacity: 0.9;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5em;
    
    &::before {
      content: '';
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: ${({ theme }) => theme.primary};
      animation: pulse 2s ease-in-out infinite;
    }
  }
  
  .progress-percent {
    font-size: 1.3em;
    font-weight: 700;
    color: ${({ theme }) => theme.primary};
    font-variant-numeric: tabular-nums;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.08)"};
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ $progress }) => $progress}%;
    background: linear-gradient(90deg, ${({ theme }) => theme.primary}, ${({ theme }) => theme.primary}dd);
    transition: width 0.3s ease;
    border-radius: 6px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1em;
  margin-top: 0.5em;
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.04)"};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 1em;
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  
  .stat-label {
    font-size: 0.75em;
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .stat-value {
    font-size: 1.5em;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
  }
`;

const LogsSection = styled.div`
  background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.02)" : "rgba(255, 255, 255, 0.03)"};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 1em;
  max-height: 300px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)"};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.primary};
    border-radius: 3px;
  }
`;

const LogsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  margin-bottom: 1em;
  padding-bottom: 0.8em;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  
  svg {
    font-size: 1.1em;
    color: ${({ theme }) => theme.primary};
  }
  
  span {
    font-size: 0.9em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }
`;

const LogEntry = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.8em;
  padding: 0.6em 0;
  border-bottom: 1px solid ${({ theme }) => theme.border3};
  
  &:last-child {
    border-bottom: none;
  }
  
  .log-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${({ type, theme }) => {
      if (type === 'success') return 'rgba(16, 185, 129, 0.15)';
      if (type === 'error') return 'rgba(239, 68, 68, 0.15)';
      return theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)";
    }};
    
    svg {
      font-size: 0.75em;
      color: ${({ type, theme }) => {
        if (type === 'success') return '#10b981';
        if (type === 'error') return '#ef4444';
        return theme.text;
      }};
      opacity: ${({ type }) => type === 'info' ? 0.6 : 1};
    }
  }
  
  .log-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.2em;
    
    .log-time {
      font-size: 0.7em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      font-family: 'Courier New', monospace;
    }
    
    .log-message {
      font-size: 0.85em;
      color: ${({ theme }) => theme.text};
      opacity: 0.9;
      line-height: 1.4;
    }
  }
`;

const DownloadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  padding-top: 1em;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6em;
  padding: 0.9em 1.2em;
  background: ${({ variant, theme }) => {
    if (variant === 'primary') return theme.primary;
    return theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.06)";
  }};
  color: ${({ variant, theme }) => variant === 'primary' ? '#fff' : theme.text};
  border: 1px solid ${({ variant, theme }) => {
    if (variant === 'primary') return theme.primary;
    return theme.border;
  }};
  border-radius: 12px;
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  svg {
    font-size: 1.1em;
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${({ variant, theme }) => {
      if (variant === 'primary') return `${theme.primary}40`;
      return theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.08)";
    }};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CompletionMessage = styled.div`
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05));
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  padding: 1.5em;
  display: flex;
  align-items: center;
  gap: 1em;
  margin-bottom: 1em;
  
  .icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(16, 185, 129, 0.2);
    border-radius: 50%;
    
    svg {
      font-size: 1.5em;
      color: #10b981;
    }
  }
  
  .content {
    flex: 1;
    
    h4 {
      font-size: 1em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      margin-bottom: 0.3em;
    }
    
    p {
      font-size: 0.85em;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
    }
  }
`;

export default function XmlDownloadTaskLog({ 
  isProcessing, 
  processingComplete, 
  taskProgress, 
  currentStage, 
  taskStats, 
  taskLogs,
  onDownload,
  downloadButtons
}) {
  const getLogIcon = (type) => {
    switch (type) {
      case 'success':
        return <FiCheckCircle />;
      case 'error':
        return <FiAlertCircle />;
      default:
        return <FiInfo />;
    }
  };

  return (
    <TaskLogContainer>
      {processingComplete && (
        <>
          <CompletionMessage>
            <div className="icon">
              <FiCheckCircle />
            </div>
            <div className="content">
              <h4>Processamento Concluído!</h4>
              <p>Os arquivos estão prontos para download</p>
            </div>
          </CompletionMessage>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1em',
            marginBottom: '1.5em'
          }}>
            <StatCard style={{minHeight: '80px'}}>
              <span className="stat-label">Total de Chaves</span>
              <span className="stat-value">{(taskStats?.totalChaves || 0).toLocaleString('pt-BR')}</span>
            </StatCard>
            <StatCard style={{minHeight: '80px'}}>
              <span className="stat-label">XMLs Encontrados</span>
              <span className="stat-value" style={{color: '#10b981'}}>{(taskStats?.arquivosEncontrados || 0).toLocaleString('pt-BR')}</span>
            </StatCard>
            <StatCard style={{minHeight: '80px'}}>
              <span className="stat-label">Chaves Faltantes</span>
              <span className="stat-value" style={{color: '#ef4444'}}>{(taskStats?.arquivosFaltantes || 0).toLocaleString('pt-BR')}</span>
            </StatCard>
          </div>
        </>
      )}

      {isProcessing && (
        <ProgressSection>
          <ProgressHeader>
            <span className="stage-text">{currentStage || 'Processando...'}</span>
            <span className="progress-percent">{Math.round(taskProgress)}%</span>
          </ProgressHeader>
          <ProgressBar $progress={taskProgress} />
          
          <StatsGrid>
            <StatCard>
              <span className="stat-label">Total</span>
              <span className="stat-value">{taskStats.totalChaves.toLocaleString('pt-BR')}</span>
            </StatCard>
            <StatCard>
              <span className="stat-label">Encontrados</span>
              <span className="stat-value">{taskStats.arquivosEncontrados.toLocaleString('pt-BR')}</span>
            </StatCard>
            <StatCard>
              <span className="stat-label">Faltantes</span>
              <span className="stat-value">{taskStats.arquivosFaltantes.toLocaleString('pt-BR')}</span>
            </StatCard>
          </StatsGrid>
        </ProgressSection>
      )}

      {taskLogs.length > 0 && (
        <LogsSection>
          <LogsHeader>
            <FiClock />
            <span>Log de Atividades</span>
          </LogsHeader>
          {taskLogs.map((log, index) => (
            <LogEntry key={index} type={log.type}>
              <div className="log-icon">
                {getLogIcon(log.type)}
              </div>
              <div className="log-content">
                <span className="log-time">{log.timestamp}</span>
                <span className="log-message">{log.message}</span>
              </div>
            </LogEntry>
          ))}
        </LogsSection>
      )}

      {processingComplete && (
        <DownloadSection>
          {downloadButtons ? downloadButtons : (
            <DownloadButton variant="primary" onClick={() => onDownload('zip')}>
              <FiArchive />
              Baixar XMLs (.zip)
            </DownloadButton>
          )}
        </DownloadSection>
      )}
    </TaskLogContainer>
  );
}
